import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthUtils } from '../utils/auth.utils';
import {catchError, from, Observable, switchMap, throwError} from 'rxjs';
import {AuthService} from "../services/auth.service";
import {environment} from "environments/environment";
import {EXTERNAL_PUBLIC_REQUEST} from './external-public-request.context';

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    if (req.context.get(EXTERNAL_PUBLIC_REQUEST) || isAuthExcludedExternalOrigin(req.url)) {
        return next(req);
    }

    const authService = inject(AuthService);

    // Helper function to clone request with current tokens
    const addAuthHeader = (request: HttpRequest<unknown>): HttpRequest<unknown> => {
        if (request.url.indexOf('/api/') > 0 && !request.url.includes('.s3.')) {
            if (
                authService.accessToken &&
                !AuthUtils.isTokenExpired(authService.accessToken) &&
                request.url.indexOf('public') < 0
            ) {
                return request.clone({
                    headers: request.headers
                        .set('Authorization', 'Bearer ' + authService.accessToken)
                        .set('X-TenantId', authService.authConnectionData.tenantId)
                        .set('X-IdentityToken', authService.identityToken)
                    ,
                });
            } else {
                return request.clone({
                    headers: request.headers
                        .set('X-TenantId', environment.defaultAuth.tennantID)
                    ,
                });
            }
        }
        return request;
    };

    const handle401Error = (request: HttpRequest<unknown>, next: HttpHandlerFn) => {
        return from(authService.refresh()).pipe(
            switchMap(() => {
                return next(addAuthHeader(request));
            }),
            catchError((err) => {
                authService.signOut();
                location.reload();
                return throwError(() => err);
            })
        );
    };

    // If token is expired and it's not a public request, try to refresh before sending
    if (
        req.url.indexOf('/api/') > 0 &&
        !req.url.includes('.s3.') &&
        req.url.indexOf('public') < 0 &&
        authService.accessToken &&
        AuthUtils.isTokenExpired(authService.accessToken)
    ) {
        return from(authService.refresh()).pipe(
            switchMap(() => {
                return next(addAuthHeader(req)).pipe(
                    catchError((error) => {
                        if (error instanceof HttpErrorResponse && error.status === 401) {
                            return handle401Error(req, next);
                        }
                        return throwError(() => error);
                    })
                );
            }),
            catchError((err) => {
                authService.signOut();
                location.reload();
                return throwError(() => err);
            })
        );
    }

    // Normal request flow
    return next(addAuthHeader(req)).pipe(
        catchError((error) => {
            // Catch "401 Unauthorized" responses
            if (error instanceof HttpErrorResponse && error.status === 401) {
                return handle401Error(req, next);
            }

            return throwError(() => error);
        })
    );
};

function isAuthExcludedExternalOrigin(requestUrl: string): boolean {
    try {
        return environment.authExcludedExternalOrigins.includes(new URL(requestUrl).origin);
    } catch {
        return false;
    }
}
